import React, {useContext, useEffect, useState} from 'react'
import {AppbarContentContext} from "../../../../commons/routing/appbar/AppbarContentProvider";
import {useStyles} from "./StatisticsSection.css";
import FormControl from "@mui/material/FormControl";
import useGeneralStatisticOverviewForm from "./useGeneralStatisticOverviewForm";
import Autocomplete from '@mui/material/Autocomplete';
import {
    IconButton,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import 'react-date-range/dist/styles.css'; // CSS file for date time picker
import 'react-date-range/dist/theme/default.css';
import {AssessmentRounded, BarChartRounded, DateRangeSharp, TimelineRounded} from "@mui/icons-material";
import LoadingProgressIndication from "../../../../commons/elements/progress/loading/LoadingProgressIndication";
import Typography from "@mui/material/Typography";
import FilterCriteriaChip from "../chip/FilterCriteriaChip";
import {useIntl} from "react-intl";
import useWriteMockedPlayerInputs from "../mocking/useWriteMockedPlayerInputs";
import StatisticsChartInfoSummary from "../chartDetails/sections/StatisticsChartInfoSummary";
import BasicChart from "../chart/BasicChart";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";

const StatisticsSection = () => {
    const classes = useStyles()

    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    const [chartLoading, setChartLoading] = useState(false)

    // Chart data as basis for rendering the chart
    const [chartData, setChartData] = useState({})

    // Internationalization for simple strings
    const intl = useIntl();

    // Only used for distinction of the selectable metrics (master data metrics not allowed for role "organizer")
    const {signedInUserRole} = useContext(FirebaseAuthContext);

    const {
        associationsFilter,
        disciplinesFilter,
        coursesFilter,
        playlistsFilter,
        usersFilter,
        expertisesFilter,
        difficultiesFilter,

        selectableAssociations,
        selectableDisciplines,
        selectableCourses,
        selectablePlaylists,
        selectableUsers,
        selectableExpertises,
        selectableDifficulties,
        selectableAggregations,

        metric,
        aggregation,
        fromToDateRange,
        chartType,

        visibleMetric,
        visibleAggregation,
        visibleChartType,

        handleChangeAssociationFilter,
        handleChangeDisciplinesFilter,
        handleChangeCoursesFilter,
        handleChangePlaylistsFilter,
        handleChangeUsersFilter,
        handleChangeExpertisesFilter,
        handleChangeDifficultyFilter,

        handleChangeMetric,
        handleChangeAggregation,
        handleChangeChartType,

        getDiagramData,

        handlePickDate,

        validSelectionSetup,
        isSelectionAllowed
    } = useGeneralStatisticOverviewForm()

    const {writePlayerInputs} = useWriteMockedPlayerInputs()

    // Setting the appbar content
    useEffect(() => {
        setCurrentAppbarContent({
            header: intl.formatMessage({id: 'app.statistics.overview.appbar.header', defaultMessage: 'Statistics'}),
            description: intl.formatMessage({
                id: 'app.statistics.overview.appbar.description',
                defaultMessage: 'Get more insights to the performance...'
            }),
            actions: <>
            </>
        })
    }, [])

    /*
      Reloading the chart after each change, but only if the selection is allowed.
      The check is necessary, because we are automatically resetting the currently selected chart type and the aggregation.
      This would trigger two executions of the statistics-fetch to the backend (one with potentially wrong parameters), which we need to avoid.
     */
    useEffect(() => {
        if (isSelectionAllowed(metric, aggregation, chartType)) {
            reloadStatistics()
        }
    }, [metric, aggregation, chartType, associationsFilter, disciplinesFilter, coursesFilter, playlistsFilter, usersFilter, expertisesFilter, difficultiesFilter, fromToDateRange])

    // Callback listener for the generating the statistics (fetching data from endpoint and render chart accordingly)
    const reloadStatistics = async () => {
        setChartLoading(true)

        // Load the basic statistical chart data (already prepared for one single chart)
        const data = await getDiagramData()
        setChartData(data || {})

        setChartLoading(false)
    }

    const getGroupedMenuItems = (groupStructureList, valueTranslationPrefix, groupTranslationPrefix) => {
        const list = []

        groupStructureList.forEach((aggregationItem) => {

            // Push group header
            list.push(
                <ListSubheader key={aggregationItem.group}
                               className={classes.menuItemHeader}>
                    <b>{intl.formatMessage({
                        id: `${groupTranslationPrefix}.${aggregationItem.group}`,
                        defaultMessage: aggregationItem.group
                    })}</b>
                </ListSubheader>)

            // Push single values for the above group
            aggregationItem.values.forEach((aggregationValue) => {
                list.push(
                    <MenuItem key={aggregationValue} value={aggregationValue}>
                        {intl.formatMessage({
                            id: `${valueTranslationPrefix}.${aggregationValue}`,
                            defaultMessage: aggregationValue
                        })}
                    </MenuItem>)
            })
        })

        return list
    }

    return (
        <div className={classes.row}>

            {/* Filter row */}
            <div className={classes.filterColumn}>

                <div className={classes.filterHeader}>
                    <Typography component="h1" variant="h4" color="inherit" noWrap>
                        {intl.formatMessage({id: 'app.statistics.filter.header', defaultMessage: 'Filter'})}
                    </Typography>
                </div>

                {/*<Button onClick={writePlayerInputs}>
                    Persist Mocked Player Inputs
                </Button>*/}


                {/* Associations [{id, name}] */}
                {validSelectionSetup[metric]?.filters.includes('associations') &&
                <FormControl className={classes.formControl}>
                    <Autocomplete
                        multiple
                        limitTags={2}
                        id="reg-statistics-associations-input"
                        options={selectableAssociations}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(association) =>
                            intl.formatMessage({
                                id: `app.ids.association.${association.id}`,
                                defaultMessage: association.id
                            })}
                        renderInput={(params) => (
                            <TextField {...params}
                                       label={associationsFilter?.length > 0
                                           ? intl.formatMessage({
                                               id: 'app.statistics.filter.form.associations.label.filtered',
                                               defaultMessage: 'Associations'
                                           })
                                           : intl.formatMessage({
                                               id: 'app.statistics.filter.form.associations.label.unfiltered',
                                               defaultMessage: 'All Associations'
                                           })}
                                       placeholder={
                                           intl.formatMessage({
                                               id: 'app.statistics.filter.form.associations.placeholder',
                                               defaultMessage: 'Associations'
                                           })}/>
                        )}
                        renderTags={(tValue, getTagProps) =>
                            <FilterCriteriaChip filteredValues={associationsFilter}
                                                selectableValues={selectableAssociations}
                                                getTagProps={getTagProps}
                                                translationPrefix='app.ids.association'/>}
                        onChange={handleChangeAssociationFilter}
                        value={associationsFilter}
                    />
                </FormControl>}

                {/* Disciplines [{id, name}] */}
                {validSelectionSetup[metric]?.filters.includes('disciplines') &&
                <FormControl className={classes.formControl} style={{paddingLeft: 15}}>
                    <Autocomplete
                        multiple
                        limitTags={2}
                        id="reg-statistics-discipline-input"
                        options={selectableDisciplines}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(discipline) =>
                            intl.formatMessage({
                                id: `app.ids.discipline.${discipline.id}`,
                                defaultMessage: discipline.id
                            })}
                        renderInput={(params) => (
                            <TextField {...params}
                                       label={disciplinesFilter?.length > 0
                                           ? intl.formatMessage({
                                               id: 'app.statistics.filter.form.disciplines.label.filtered',
                                               defaultMessage: 'Disciplines'
                                           })
                                           : intl.formatMessage({
                                               id: 'app.statistics.filter.form.disciplines.label.unfiltered',
                                               defaultMessage: 'All Disciplines'
                                           })}
                                       placeholder={
                                           intl.formatMessage({
                                               id: 'app.statistics.filter.form.disciplines.placeholder',
                                               defaultMessage: 'Disciplines'
                                           })}/>
                        )}
                        renderTags={(tValue, getTagProps) =>
                            <FilterCriteriaChip filteredValues={disciplinesFilter}
                                                selectableValues={selectableDisciplines}
                                                getTagProps={getTagProps}
                                                translationPrefix='app.ids.discipline'/>}
                        onChange={handleChangeDisciplinesFilter}
                        value={disciplinesFilter}
                    />
                </FormControl>}

                {/* Courses [{id, name, groupBy}] */}
                {validSelectionSetup[metric]?.filters.includes('courses') &&
                <FormControl className={classes.formControl} style={{paddingLeft: 30}}>
                    <Autocomplete
                        multiple
                        limitTags={2}
                        id="reg-statistics-courses-input"
                        options={selectableCourses}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        groupBy={(option) => option.groupBy}
                        renderInput={(params) => (
                            <TextField {...params}
                                       label={coursesFilter?.length > 0
                                           ? intl.formatMessage({
                                               id: 'app.statistics.filter.form.courses.label.filtered',
                                               defaultMessage: 'Courses'
                                           })
                                           : intl.formatMessage({
                                               id: 'app.statistics.filter.form.courses.label.unfiltered',
                                               defaultMessage: 'All Courses'
                                           })}
                                       placeholder={
                                           intl.formatMessage({
                                               id: 'app.statistics.filter.form.courses.placeholder',
                                               defaultMessage: 'Courses'
                                           })}/>
                        )}
                        renderTags={(tValue, getTagProps) =>
                            <FilterCriteriaChip filteredValues={coursesFilter} selectableValues={selectableCourses}
                                                getTagProps={getTagProps}/>}
                        onChange={handleChangeCoursesFilter}
                        value={coursesFilter}
                    />
                </FormControl>}

                {/* Playlists[{id, name, groupBy}] */}
                {validSelectionSetup[metric]?.filters.includes('playlists') &&
                <FormControl className={classes.formControl} style={{paddingLeft: 45}}>
                    <Autocomplete
                        multiple
                        limitTags={2}
                        id="reg-statistics-playlist-input"
                        options={selectablePlaylists}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        groupBy={(option) => option.groupBy}
                        renderInput={(params) => (
                            <TextField {...params}
                                       label={playlistsFilter?.length > 0
                                           ? intl.formatMessage({
                                               id: 'app.statistics.filter.form.playlists.label.filtered',
                                               defaultMessage: 'Playlists'
                                           })
                                           : intl.formatMessage({
                                               id: 'app.statistics.filter.form.playlists.label.unfiltered',
                                               defaultMessage: 'All Playlists'
                                           })}
                                       placeholder={
                                           intl.formatMessage({
                                               id: 'app.statistics.filter.form.playlists.placeholder',
                                               defaultMessage: 'Playlists'
                                           })}/>
                        )}
                        renderTags={(tValue, getTagProps) =>
                            <FilterCriteriaChip filteredValues={playlistsFilter} selectableValues={selectablePlaylists}
                                                getTagProps={getTagProps}/>}
                        onChange={handleChangePlaylistsFilter}
                        value={playlistsFilter}
                    />
                </FormControl>}

                {/* Users [{id, name, groupBy}] */}
                {validSelectionSetup[metric]?.filters.includes('users') &&
                <FormControl className={classes.formControl} style={{paddingLeft: 45}}>
                    <Autocomplete
                        multiple
                        limitTags={2}
                        id="reg-statistics-users-input"
                        options={selectableUsers}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        groupBy={(option) => option.groupBy}
                        renderInput={(params) => (
                            <TextField {...params}
                                       label={usersFilter?.length > 0
                                           ? intl.formatMessage({
                                               id: 'app.statistics.filter.form.users.label.filtered',
                                               defaultMessage: 'Users'
                                           })
                                           : intl.formatMessage({
                                               id: 'app.statistics.filter.form.users.label.unfiltered',
                                               defaultMessage: 'All Users'
                                           })}
                                       placeholder={
                                           intl.formatMessage({
                                               id: 'app.statistics.filter.form.users.placeholder',
                                               defaultMessage: 'Users'
                                           })}/>
                        )}
                        renderTags={(tValue, getTagProps) =>
                            <FilterCriteriaChip filteredValues={usersFilter} selectableValues={selectableUsers}
                                                getTagProps={getTagProps}/>}
                        onChange={handleChangeUsersFilter}
                        value={usersFilter}
                    />
                </FormControl>}

                {/* Expertises [{id, name}] */}
                {validSelectionSetup[metric]?.filters.includes('expertises') &&
                <FormControl className={classes.formControl} style={{paddingLeft: 60}}>
                    <Autocomplete
                        multiple
                        limitTags={2}
                        id="reg-statistics-expertises-input"
                        options={selectableExpertises}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(expertise) =>
                            intl.formatMessage({
                                id: `app.ids.expertise.${expertise.id}`,
                                defaultMessage: expertise.id
                            })}
                        renderInput={(params) => (
                            <TextField {...params}
                                       label={expertisesFilter?.length > 0
                                           ? intl.formatMessage({
                                               id: 'app.statistics.filter.form.expertises.label.filtered',
                                               defaultMessage: 'Expertises'
                                           })
                                           : intl.formatMessage({
                                               id: 'app.statistics.filter.form.expertises.label.unfiltered',
                                               defaultMessage: 'All Expertises'
                                           })}
                                       placeholder={
                                           intl.formatMessage({
                                               id: 'app.statistics.filter.form.expertises.placeholder',
                                               defaultMessage: 'Expertises'
                                           })}/>
                        )}
                        renderTags={(tValue, getTagProps) =>
                            <FilterCriteriaChip filteredValues={expertisesFilter}
                                                selectableValues={selectableExpertises}
                                                getTagProps={getTagProps}
                                                translationPrefix='app.ids.expertise'/>}
                        onChange={handleChangeExpertisesFilter}
                        value={expertisesFilter}
                    />
                </FormControl>}

                {/* Difficulties [{id, name}] */}
                {validSelectionSetup[metric]?.filters.includes('difficulties') &&
                <FormControl className={classes.formControl}>
                    <Autocomplete
                        multiple
                        limitTags={2}
                        id="reg-statistics-difficulties-input"
                        options={selectableDifficulties}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(difficulty) =>
                            intl.formatMessage({
                                id: `app.ids.difficulty.${difficulty.id}`,
                                defaultMessage: difficulty.id
                            })}
                        renderInput={(params) => (
                            <TextField {...params}
                                       label={difficultiesFilter?.length > 0
                                           ? intl.formatMessage({
                                               id: 'app.statistics.filter.form.difficulties.label.filtered',
                                               defaultMessage: 'Difficulties'
                                           })
                                           : intl.formatMessage({
                                               id: 'app.statistics.filter.form.difficulties.label.unfiltered',
                                               defaultMessage: 'All Difficulties'
                                           })}
                                       placeholder={
                                           intl.formatMessage({
                                               id: 'app.statistics.filter.form.difficulties.placeholder',
                                               defaultMessage: 'Difficulties'
                                           })}/>
                        )}
                        renderTags={(tValue, getTagProps) =>
                            <FilterCriteriaChip filteredValues={difficultiesFilter}
                                                selectableValues={selectableDifficulties}
                                                getTagProps={getTagProps}
                                                translationPrefix='app.ids.difficulty'/>}
                        onChange={handleChangeDifficultyFilter}
                        value={difficultiesFilter}
                    />
                </FormControl>}
            </div>

            {/* Chart configuration row + actual chart */}
            <div className={classes.contentColumn}>

                <div className={classes.row}>
                    <div>
                        <b>{intl.formatDate(fromToDateRange[0]) + ' - ' + intl.formatDate(fromToDateRange[1])}</b>
                        <IconButton onClick={handlePickDate} size="large">
                            <DateRangeSharp/>
                        </IconButton>
                    </div>

                    {/* Metric */}
                    <FormControl className={classes.formControlChart}>
                        <InputLabel id="reg-statistics-metric-label" className={classes.dropdownLabel}>
                            {intl.formatMessage({
                                id: 'app.statistics.filter.form.metric.label',
                                defaultMessage: 'Metric'
                            })}
                        </InputLabel>
                        <Select id="reg-statistics-metric-input" labelId="reg-statistics-metric-label"
                                value={metric} onChange={handleChangeMetric}>

                            {/* For admins all metrics are selectable - for course organizers the master data metrics are suppressed */}
                            {getGroupedMenuItems(
                                [
                                    {
                                        group: 'groupPerformance',
                                        values: ['decisionAccuracy', 'reactionTime', 'trainingIntensity']
                                    },
                                    ...(signedInUserRole !== 'organizer' ? [{
                                        group: 'groupObjects',
                                        values: ['createdScoredScenes', 'createdUsers']
                                    }] : []),
                                    {
                                        group: 'groupFeedback',
                                        values: ['createdChallengedDecisions']
                                    }
                                ],
                                'app.statistics.filter.form.metric.values',
                                'app.statistics.filter.form.metric.groups')}
                        </Select>
                    </FormControl>

                    {/* Aggregation */}
                    <FormControl className={classes.formControlChart}>
                        <InputLabel id="reg-statistics-aggregation-label" className={classes.dropdownLabel}>
                            {intl.formatMessage({
                                id: 'app.statistics.filter.form.aggregation.label',
                                defaultMessage: 'Aggregation'
                            })}
                        </InputLabel>
                        <Select id="reg-statistics-aggregation-input" labelId="reg-statistics-aggregation-label"
                                value={aggregation} onChange={handleChangeAggregation}>
                            {getGroupedMenuItems(
                                selectableAggregations,
                                'app.statistics.filter.form.aggregation.values',
                                'app.statistics.filter.form.aggregation.groups')}
                        </Select>
                    </FormControl>

                    {/* The run button has become obsolute, because we reload on each change now - uncomment again if required again */}
                    {/*<div className={classes.iconDescriptionColumn}>
                        <IconButton onClick={reloadStatistics} size="large">
                            <PlayCircleOutline className={classes.icon}/>
                        </IconButton>
                        <div style={{marginTop: -10}}>
                            {intl.formatMessage({
                                id: 'app.statistics.chart.run.label',
                                defaultMessage: 'Run'
                            })}
                        </div>
                    </div>*/}

                    {/* Chart type */}
                    <ToggleButtonGroup className={classes.centeredRow}
                                       value={chartType}
                                       exclusive
                                       onChange={handleChangeChartType}
                                       aria-label="chart-type">

                        {validSelectionSetup[metric]?.chartTypes.includes('total') &&
                        <ToggleButton className={classes.toggleButton} title='Total' value="total"
                                      aria-label="total">
                            <BarChartRounded/>
                        </ToggleButton>}

                        {validSelectionSetup[metric]?.chartTypes.includes('average') &&
                        <ToggleButton className={classes.toggleButton} title='Average' value="average"
                                      aria-label="average">
                            <AssessmentRounded/>
                        </ToggleButton>}

                        {validSelectionSetup[metric]?.chartTypes.includes('accumulated') &&
                        <ToggleButton className={classes.toggleButton} title='Accumulated' value="accumulated"
                                      aria-label="accumulated">
                            <TimelineRounded/>
                        </ToggleButton>}
                    </ToggleButtonGroup>
                </div>

                {chartLoading && <LoadingProgressIndication
                    text={intl.formatMessage({
                        id: 'app.statistics.chart.loading',
                        defaultMessage: 'Loading chart...'
                    })}/>}

                <div hidden={chartLoading} style={{width: "100%"}}>
                    <BasicChart chartId="statisticsChart"
                                chartData={chartData}/>
                </div>

                <StatisticsChartInfoSummary metric={visibleMetric}
                                            aggregation={visibleAggregation}
                                            chartType={visibleChartType}/>
            </div>

        </div>
    );
}
export default StatisticsSection
